import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { getUnitAvailability, RawUnitStub } from '@/api/units';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import type { UnitAvailability, CartItem } from '@/types';

export type UnitListItem = RawUnitStub;

type CalendarContextType = {
  unitList: UnitListItem[];
  unitAvailability: UnitAvailability[];
  selectedUnit: string;
  cartItems: CartItem[];
  isLoading: boolean;
  setSelectedUnit: (roomNumber: string) => void;
  addToCart: (item: CartItem) => void;
  removeFromCart: (roomNumber: string, week: number, year: number) => void;
  clearCart: () => void;
  refreshAvailability: () => Promise<void>;
};

const CalendarContext = createContext<CalendarContextType | undefined>(undefined);

export function CalendarProvider({ children }: { children: React.ReactNode }) {
  const [allAvailability, setAllAvailability] = useState<UnitAvailability[]>([]);
  const [unitList, setUnitList] = useState<UnitListItem[]>([]);
  const [unitAvailability, setUnitAvailability] = useState<UnitAvailability[]>([]);
  const [selectedUnit, setSelectedUnit] = useState<string>('');
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { user, isLoading: isAuthLoading } = useAuth();

  const fetchAllAvailability = useCallback(async () => {
    try {
      setIsLoading(true);
      const { availabilities, units } = await getUnitAvailability();
      setAllAvailability(availabilities);
      setUnitList(units);
    } catch (error) {
      console.error('Error fetching availability:', error);
      toast.error('Failed to fetch availability data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refreshAvailability = useCallback(async () => {
    await fetchAllAvailability();
  }, [fetchAllAvailability]);

  // Filter availability for selected room
  useEffect(() => {
    if (selectedUnit) {
      setUnitAvailability(allAvailability.filter(a => a.number === selectedUnit));
    } else {
      setUnitAvailability([]);
    }
  }, [selectedUnit, allAvailability]);

  // Auto-select first unit once data loads
  useEffect(() => {
    if (unitList.length > 0 && !selectedUnit) {
      setSelectedUnit(unitList[0].number);
    }
  }, [unitList, selectedUnit]);

  useEffect(() => {
    if (!isAuthLoading && user) {
      fetchAllAvailability();
    }
  }, [isAuthLoading, user, fetchAllAvailability]);

  const addToCart = useCallback((item: CartItem) => {
    setCartItems(prev => {
      const existingIndex = prev.findIndex(
        i => i.roomNumber === item.roomNumber && i.week === item.week
      );
      if (existingIndex >= 0) {
        const newItems = [...prev];
        newItems[existingIndex] = {
          ...newItems[existingIndex],
          years: [...new Set([...newItems[existingIndex].years, ...item.years])],
        };
        return newItems;
      }
      return [...prev, item];
    });
  }, []);

  const removeFromCart = useCallback((roomNumber: string, week: number, year: number) => {
    setCartItems(prev =>
      prev
        .map(item => {
          if (item.roomNumber === roomNumber && item.week === week) {
            const newYears = item.years.filter(y => y !== year);
            return newYears.length ? { ...item, years: newYears } : null;
          }
          return item;
        })
        .filter(Boolean) as CartItem[]
    );
  }, []);

  const clearCart = useCallback(() => {
    setCartItems([]);
  }, []);

  const value = useMemo(() => ({
    unitList,
    unitAvailability,
    selectedUnit,
    cartItems,
    isLoading,
    setSelectedUnit,
    addToCart,
    removeFromCart,
    clearCart,
    refreshAvailability,
  }), [
    unitList,
    unitAvailability,
    selectedUnit,
    cartItems,
    isLoading,
    addToCart,
    removeFromCart,
    clearCart,
    refreshAvailability,
  ]);

  return (
    <CalendarContext.Provider value={value}>
      {children}
    </CalendarContext.Provider>
  );
}

export const useCalendar = () => {
  const context = useContext(CalendarContext);
  if (!context) {
    throw new Error('useCalendar must be used within a CalendarProvider');
  }
  return context;
};
